import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef,
  Input, OnDestroy, OnInit, Output, SecurityContext, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { flatten } from 'lodash';
import { Subscription } from 'rxjs';
import { VersionedResourceId } from 'src/app/sakp/model/sakp-versioned-id';
import { Violation } from 'src/app/sakp/model/violation';
import { ViolationService } from 'src/app/sakp/services/violations.service';
import { TranslateService } from '@ngx-translate/core';
import { toVisibleDateTimeFormat } from 'src/app/shared/util/date-util';
import { Moment } from 'moment';

// Etykiety powtarzalne w każdym wierszu listy. Trzymane w komponencie, bo `translate` jest
// pipe'em nieczystym — 13 jego instancji w wierszu × tysiące wierszy przeliczałoby się
// w każdym cyklu change detection.
const ROW_LABEL_KEYS = {
  vehicle: 'sakp.disputes.filed.claim-card.departure-info.vehicle',
  day: 'sakp.disputes.filed.claim-card.departure-info.day',
  task: 'sakp.disputes.filed.claim-card.departure-info.task',
  line: 'sakp.disputes.filed.claim-card.departure-info.line',
  direction: 'sakp.disputes.filed.claim-card.departure-info.direction',
  stopName: 'sakp.disputes.filed.claim-card.departure-info.stop-name',
  stopTime: 'sakp.disputes.filed.claim-card.departure-info.stop-time',
  courseTime: 'sakp.disputes.filed.claim-card.departure-info.course-time',
  decisionRejected: 'sakp.disputes.filed.decision.REJECTED',
  decisionAccepted: 'sakp.disputes.filed.decision.ACCEPTED',
  decisionRevoke: 'sakp.disputes.filed.decision.REVOKE',
  noVehicle: 'sakp.common.lack'
};

type RowLabels = Record<keyof typeof ROW_LABEL_KEYS, string>;

@Component({
  selector: 'app-departure-by-violation',
  templateUrl: './departure-by-violation.component.html',
  styleUrls: ['./departure-by-violation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DepartureByViolationComponent),
      multi: true
    }
  ]
})
export class DepartureByViolationComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  rowLabels: RowLabels = { ...ROW_LABEL_KEYS };
  private rowLabelsSubscription: Subscription;

  /** Wysokość wiersza (z odstępem) dla wirtualnego scrolla. Wartość startowa, korygowana pomiarem. */
  itemSize = 65;
  /**
   * Wirtualny scroll wymaga jednakowej wysokości wierszy, a pasek z informacją o nadpisanej decyzji
   * ją zmienia. Dlatego rezerwujemy go we wszystkich kartach, ale tylko gdy w ogóle występuje.
   */
  reserveDecisionPanel = false;
  visibleDepartures: DeparturesByViolationViewModel[] = [];

  private inputData: Record<number, DeparturesByViolationViewModel[]> = {};
  /** Sanityzacja symbolu raz na uchybienie, nie raz na wiersz listy. */
  private violationSymbols = new Map<number, SafeHtml>();

  private selectedDepartures: DeparturesByViolationFormValue[] = [];
  /** O(1) lookup: key = `${idViolation}@${id}` */
  private selectedSet = new Set<string>();
  /** Cached count per violation id; null key = total count */
  private countCache = new Map<number | null, number>();
  private rowMeasureTimeout: ReturnType<typeof setTimeout>;

  @Input()
  set departures(data: Record<number, DeparturesByViolationViewModel[]>) {
    this.inputData = data || {};
    this.violationsIds = Object.keys(this.inputData).map(Number);
    this.allDepartures = flatten(Object.values(this.inputData));
    this.reserveDecisionPanel = this.allDepartures.some(it => !!it.decisionOverride);
    this.updateVisibleDepartures();
    this.rebuildCountCache();
    this.cdr.markForCheck();
    this.scheduleRowMeasure();
  }

  get departures(): Record<number, DeparturesByViolationViewModel[]> {
    return this.inputData;
  }

  selectedIdViolation: number | null = null;
  violations: Violation[] = [];
  disabled: boolean;
  violationsIds: number[] = [];
  allDepartures: DeparturesByViolationViewModel[] = [];

  @Input() canDecide: boolean;
  @Input() disableDecisionButtons: boolean = true;

  /**
   * Decyzja całego odwołania. Dla uchybień bez własnego nadpisania to ona jest decyzją wynikową,
   * więc karta bierze z niej kolor krawędzi.
   *
   * Uwaga na dwa słowniki tej samej decyzji: tutaj, jak w przyciskach decyzji, akceptacja nazywa
   * się `ACCEPTED`, a w `decisionOverride`, które przychodzi z serwisu — `APPROVED`
   * (patrz FIXME przy enumie `Decision`).
   */
  @Input() disputeDecision: 'ACCEPTED' | 'REJECTED' | null = null;

  private onChange = (_value: DeparturesByViolationFormValue[]): void => { };
  private onTouched = (): void => { };

  @Output()
  decisionButtonClick = new EventEmitter<{claims: string[]; decision?: 'ACCEPTED' | 'REJECTED' | null; comment?: string | null}>();

  @ViewChild(CdkVirtualScrollViewport)
  private viewport: CdkVirtualScrollViewport;

  constructor(
    private violationsService: ViolationService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    this.violations = this.violationsService.getByDate(new Date())?.violations || [];
    this.violationSymbols = new Map(this.violations.map(v => [v.id, this.toSafeSymbol(v.symbol)]));

    // stream() emituje od razu i ponownie po każdej zmianie języka.
    this.rowLabelsSubscription = this.translateService.stream(Object.values(ROW_LABEL_KEYS))
      .subscribe((translated: Record<string, string>) => {
        this.rowLabels = this.buildRowLabels(translated);
        this.cdr.markForCheck();
      });
  }

  private buildRowLabels(translated: Record<string, string>): RowLabels {
    const labels = {} as RowLabels;
    for (const name of Object.keys(ROW_LABEL_KEYS))
      labels[name] = translated[ROW_LABEL_KEYS[name]];
    // W szablonie było `| translate | capitalizeFirst`.
    if (labels.noVehicle)
      labels.noVehicle = labels.noVehicle.charAt(0).toUpperCase() + labels.noVehicle.slice(1);
    return labels;
  }

  ngAfterViewInit(): void {
    this.scheduleRowMeasure();
  }

  ngOnDestroy(): void {
    this.rowLabelsSubscription?.unsubscribe();
    clearTimeout(this.rowMeasureTimeout);
  }

  // Wysokość wiersza zależy od rozmiaru czcionki i obecności paska decyzji, więc zamiast wpisywać ją
  // na sztywno mierzymy pierwszy wyrenderowany wiersz i z niego wyliczamy krok wirtualnego scrolla.
  private scheduleRowMeasure(): void {
    clearTimeout(this.rowMeasureTimeout);
    this.rowMeasureTimeout = setTimeout(() => this.measureRowHeight());
  }

  private measureRowHeight(): void {
    const rows = this.viewport?.elementRef.nativeElement.querySelectorAll<HTMLElement>('.departure-wrapper');
    if (rows == null || rows.length === 0)
      return;
    // Różnica pozycji dwóch kolejnych wierszy uwzględnia też margines, więc jest dokładnym krokiem.
    // Pozycje z `getBoundingClientRect`, a nie `offsetTop`: wysokość wiersza wypada ułamkowa
    // (~77.375px), a zaokrąglenie do pełnych pikseli rozjeżdżałoby odstępy między kartami.
    const first = rows[0].getBoundingClientRect();
    const stride = rows.length > 1
      ? rows[1].getBoundingClientRect().top - first.top
      : first.height;
    if (stride > 0 && Math.abs(stride - this.itemSize) >= 0.5) {
      this.itemSize = stride;
      this.cdr.markForCheck();
    }
  }

  private updateVisibleDepartures(): void {
    this.visibleDepartures = this.selectedIdViolation == null
      ? this.allDepartures
      : (this.inputData[this.selectedIdViolation] || []);
  }

  private selKey(idViolation: number, id: string): string {
    return `${idViolation}@${id}`;
  }

  private rebuildSelectedSet(): void {
    this.selectedSet = new Set(
      this.selectedDepartures.map(d => this.selKey(d.idViolation, d.id.id))
    );
    this.rebuildCountCache();
  }

  private rebuildCountCache(): void {
    this.countCache.clear();
    this.countCache.set(null, this.selectedDepartures.length);
    for (const idViolation of this.violationsIds) {
      const deps = this.inputData[idViolation] || [];
      const count = deps.filter(d => this.selectedSet.has(this.selKey(d.idViolation, d.id))).length;
      this.countCache.set(idViolation, count);
    }
  }

  toggleSelectedIdViolation(idViolation: number): void {
    this.selectedIdViolation = idViolation === this.selectedIdViolation ? null : idViolation;
    this.updateVisibleDepartures();
    this.viewport?.scrollToOffset(0);
  }

  get getNameToDisplay(): string {
    if (this.selectedIdViolation == null)
      return 'sakp.departures.add-reclamation-dialog.all-violations';
    const violation = this.violations.find(it => it.id === this.selectedIdViolation);
    if (!violation) return 'unknown';
    const currentLang = this.translateService.currentLang || 'en';
    return violation.i18n?.[currentLang]?.name || violation.name || 'unknown';
  }

  // Treść przechodzi przez sanitizer, więc oznaczenie jako SafeHtml nie omija zabezpieczeń,
  // a pozwala uniknąć ponownej sanityzacji przy każdym wiązaniu [innerHTML].
  private toSafeSymbol(symbol: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.sanitizer.sanitize(SecurityContext.HTML, symbol) ?? '');
  }

  getViolationSymbol(idViolation: number): SafeHtml {
    let symbol = this.violationSymbols.get(idViolation);
    if (symbol == null) {
      symbol = this.toSafeSymbol(idViolation + '');
      this.violationSymbols.set(idViolation, symbol);
    }
    return symbol;
  }

  /**
   * Decyzja wynikowa uchybienia: własne nadpisanie, a gdy go nie ma — decyzja całego odwołania.
   * Zwraca od razu nazwę klasy, bo jedynym odbiorcą jest kolor krawędzi karty.
   */
  decisionClass(departure: DeparturesByViolationViewModel): string {
    const override = departure.decisionOverride;
    if (override != null)
      return override === 'APPROVED' ? 'decision-approved' : 'decision-rejected';
    if (this.disputeDecision != null)
      return this.disputeDecision === 'ACCEPTED' ? 'decision-approved' : 'decision-rejected';
    return 'decision-pending';
  }

  createAdditionalInfo(departure: DeparturesByViolationViewModel): string[] {
    if (departure == null)
      return ['ERROR'];
    return [departure.line, departure.task, departure.time, departure.stop];
  }

  toggleSelectedDeparture(value: DeparturesByViolationViewModel, state: boolean): void {
    const key = this.selKey(value.idViolation, value.id);
    if (state) {
      if (!this.selectedSet.has(key)) {
        this.selectedDepartures = [...this.selectedDepartures, value.toFormValue()];
        this.selectedSet.add(key);
        this.rebuildCountCache();
      }
    } else {
      this.selectedDepartures = this.selectedDepartures.filter(
        d => !(d.id.id === value.id && value.idViolation === d.idViolation)
      );
      this.selectedSet.delete(key);
      this.rebuildCountCache();
    }
    this.onChange(this.selectedDepartures);
    this.cdr.markForCheck();
  }

  isDepartureSelected(departure: DeparturesByViolationViewModel): boolean {
    return this.selectedSet.has(this.selKey(departure.idViolation, departure.id));
  }

  writeValue(deps: DeparturesByViolationFormValue[]): void {
    this.selectedDepartures = deps || [];
    this.rebuildSelectedSet();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  setCanDecide(canDecide: boolean): void {
    this.canDecide = canDecide;
  }

  toggleAllSelectedForViolation(idViolation: number, state: boolean): void {
    const allByViolation = this.departures[idViolation] || [];
    if (state) {
      const toAdd: DeparturesByViolationFormValue[] = [];
      for (const item of allByViolation) {
        const key = this.selKey(item.idViolation, item.id);
        if (!this.selectedSet.has(key)) {
          toAdd.push(item.toFormValue());
          this.selectedSet.add(key);
        }
      }
      if (toAdd.length > 0) {
        this.selectedDepartures = [...this.selectedDepartures, ...toAdd];
      }
    } else {
      const toRemoveKeys = new Set(allByViolation.map(item => this.selKey(item.idViolation, item.id)));
      this.selectedDepartures = this.selectedDepartures.filter(
        d => !toRemoveKeys.has(this.selKey(d.idViolation, d.id.id))
      );
      for (const item of allByViolation) {
        this.selectedSet.delete(this.selKey(item.idViolation, item.id));
      }
    }
    this.rebuildCountCache();
    this.onChange(this.selectedDepartures);
    this.cdr.markForCheck();
  }

  isAllSelectedByViolation(idViolation: number): boolean {
    const total = this.departures[idViolation]?.length ?? 0;
    return total > 0 && (this.countCache.get(idViolation) ?? 0) === total;
  }

  toggleAllViolations(state: boolean): void {
    if (state) {
      const toAdd: DeparturesByViolationFormValue[] = [];
      for (const k of this.violationsIds) {
        for (const item of (this.departures[k] || [])) {
          const key = this.selKey(item.idViolation, item.id);
          if (!this.selectedSet.has(key)) {
            toAdd.push(item.toFormValue());
            this.selectedSet.add(key);
          }
        }
      }
      if (toAdd.length > 0) {
        this.selectedDepartures = [...this.selectedDepartures, ...toAdd];
      }
    } else {
      this.selectedDepartures = [];
      this.selectedSet.clear();
    }
    this.rebuildCountCache();
    this.onChange(this.selectedDepartures);
    this.cdr.markForCheck();
  }

  isSelectedAllViolations(): boolean {
    return this.violationsIds.length > 0 &&
      this.allDepartures.length === this.selectedDepartures.length;
  }

  countSelected(idOfSelectedAllViolation: number | null): number {
    return this.countCache.get(idOfSelectedAllViolation) ?? 0;
  }

  handleClickOverrideDecision(idClaim: string, decision: 'ACCEPTED' | 'REJECTED' | null): void {
    if (!this.disableDecisionButtons)
      this.decisionButtonClick.emit(({claims:[idClaim], decision, comment: null}));
  }

  handleClickRemoveDecisionOverride(idClaim: string): void {
    if (!this.disableDecisionButtons)
      this.decisionButtonClick.emit(({claims:[idClaim], decision: null}));
  }

  departureTrackBy = (_index: number, dep: DeparturesByViolationViewModel): string => dep.id;

  toVisibleDateTime(date: Moment | Date): string {
    return toVisibleDateTimeFormat(date);
  }
}

export class DeparturesByViolationViewModel {
  constructor(
    public readonly id: string,
    public readonly version: number,
    public readonly idViolation: number,
    public readonly vehicle: string,
    public readonly day: string,
    public readonly task: string,
    public readonly line: string,
    public readonly direction: string,
    public readonly course: string,
    public readonly time: string,
    public readonly stop: string,
    public readonly idClaim: string | null,
    public decisionOverride: 'APPROVED' | 'REJECTED' | null,
    public decisionOverridenAt: Date | null,
    public decisionOverridenBy: string | null
  ) { }

  toFormValue(): DeparturesByViolationFormValue {
    return new DeparturesByViolationFormValue(new VersionedResourceId(this.id, this.version), this.idViolation, this.idClaim);
  }

}

export class DeparturesByViolationFormValue {
  constructor(public readonly id: VersionedResourceId, public readonly idViolation: number, public readonly idClaim: string) { }
}
